import { createClient } from '@supabase/supabase-js';
import customStorageAdapter from './customStorageAdapter.js';
import env from './environment.js';
import { isTransientNetworkError } from './errorUtils.js';
import logger from './logger.js';

// Check if we're in a browser/React Native environment
const isBrowser = typeof window !== 'undefined';

// Methods that are safe to retry on a transient network blip. Only idempotent
// reads — never POST/PATCH/DELETE (could double-apply) and never auth token
// refresh. This catches the common mobile-Safari "Load failed" on a .select()
// (e.g. getUserProfile, NegotiationContext counts) at the transport layer so
// callers don't surface an empty result for a one-off connectivity hiccup.
const RETRYABLE_METHODS = new Set(['GET', 'HEAD']);
const FETCH_RETRY_DELAY_MS = 800;

const fetchWithReadRetry = async (input, init = {}) => {
  const method = (
    init?.method ||
    (input && typeof input !== 'string' && input.method) ||
    'GET'
  ).toUpperCase();
  try {
    return await fetch(input, init);
  } catch (err) {
    if (RETRYABLE_METHODS.has(method) && isTransientNetworkError(err)) {
      logger.warn(`[Supabase] Transient network error on ${method} — retrying once in ${FETCH_RETRY_DELAY_MS}ms`);
      await new Promise((resolve) => setTimeout(resolve, FETCH_RETRY_DELAY_MS));
      return await fetch(input, init);
    }
    throw err;
  }
};

// Custom lock function that wraps navigator.locks with a 5-second timeout.
// supabase-js uses navigator.locks with acquireTimeout=-1 (wait forever),
// which can deadlock on hard refresh or React StrictMode double-mount when
// multiple getSession() calls queue up behind a stale lock.
const navigatorLockWithTimeout = async (name, acquireTimeout, fn) => {
  if (isBrowser && globalThis?.navigator?.locks) {
    const timeout = acquireTimeout > 0 ? acquireTimeout : 5000; // default 5s instead of forever
    const abortController = new AbortController();
    const timer = setTimeout(() => abortController.abort(), timeout);

    try {
      return await navigator.locks.request(
        name,
        { mode: 'exclusive', signal: abortController.signal },
        async (lock) => {
          clearTimeout(timer);
          if (lock) {
            return await fn();
          }
          // Lock not acquired (shouldn't happen without ifAvailable), run anyway
          return await fn();
        }
      );
    } catch (err) {
      clearTimeout(timer);
      if (err.name === 'AbortError') {
        // Lock acquire timed out — run the function without the lock.
        // This prevents deadlocks at the cost of a rare concurrent token refresh.
        logger.warn('[Supabase] Lock acquire timed out for', name, '— running without lock');
        return await fn();
      }
      throw err;
    }
  }
  // Fallback: no navigator.locks support
  return await fn();
};

// Strip auth error hashes (e.g. #error=access_denied&error_code=otp_expired) from the URL
// BEFORE the Supabase client initializes. Without this, detectSessionInUrl parses the error
// hash and destroys the existing valid session — logging out users who click an expired
// magic link while already authenticated.
if (isBrowser && window.location.hash.includes('error=access_denied')) {
  logger.warn('[Supabase] Stripping auth error hash to protect existing session:', window.location.hash.substring(0, 80));
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
}

// Create a browser-safe Supabase client configuration
const supabaseConfig = {
  auth: {
    storage: customStorageAdapter,
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    lock: navigatorLockWithTimeout,
  },
  // Retry idempotent reads once on a transient network failure (see
  // fetchWithReadRetry). Writes and auth calls fall through unchanged.
  global: { fetch: fetchWithReadRetry },
};

// Create Supabase client with SSR-safe configuration
const supabase = createClient(env.supabase.url, env.supabase.anonKey, supabaseConfig);

// Initialize auth state listener - only in browser environments
// NOTE: This function is kept for backwards compatibility but no longer sets up a listener.
// Auth state is now handled exclusively by AuthContext to prevent duplicate event processing
// and race conditions that were causing duplicate job creation during email confirmation.
const initAuth = () => {
  // Skip initialization during server-side rendering
  if (!isBrowser) {
    logger.log('Skipping auth initialization in non-browser environment');
  } else {
    logger.log(
      '[initAuth] Auth state management handled by AuthContext - no duplicate listener needed'
    );
  }

  // Return a no-op subscription for backwards compatibility
  return {
    unsubscribe: () => {
      // No-op: intentionally empty - no listener to unsubscribe
    },
  };
};

export { initAuth };
export default supabase;
